import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Autocomplete,
  CircularProgress,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { updateInitiative, resetUpdateState } from "../../../redux/initiatives";
import { toast } from "react-toastify";
import { format, parseISO } from "date-fns";

const EditInitiativeModal = ({ open, onClose, initiative, users }) => {
  const dispatch = useDispatch();
  const { update } = useSelector((state) => state.Initiatives);

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    owner_id: null,
    secondary_owner_id: null,
    status: "future",
    priority: "medium",
    objective_completion_date: null,
  });

  useEffect(() => {
    if (initiative) {
      setFormData({
        title: initiative.title || "",
        description: initiative.description || "",
        owner_id: initiative.owner_id || null,
        secondary_owner_id: initiative.secondary_owner_id || null,
        status: initiative.status || "future",
        priority: initiative.priority || "medium",
        objective_completion_date: initiative.objective_completion_date
          ? parseISO(initiative.objective_completion_date)
          : null,
      });
    }
  }, [initiative]);

  const handleChange = (field) => (e) => {
    setFormData({ ...formData, [field]: e.target.value });
  };

  const handleOwnerChange = (field) => (event, value) => {
    setFormData({ ...formData, [field]: value?.id || null });
  };

  const handleDateChange = (date) => {
    setFormData({ ...formData, objective_completion_date: date });
  };

  const handleSubmit = async () => {
    if (!formData.title.trim()) {
      toast.error("Title is required");
      return;
    }
    if (!formData.owner_id) {
      toast.error("Owner is required");
      return;
    }

    const data = {
      ...formData,
      objective_completion_date: formData.objective_completion_date
        ? format(new Date(formData.objective_completion_date), "yyyy-MM-dd")
        : null,
    };

    const result = await dispatch(updateInitiative({ id: initiative.id, data }));
    if (result.meta.requestStatus === "fulfilled") {
      toast.success("Initiative updated successfully");
      handleClose();
    } else {
      toast.error(result.payload || "Failed to update initiative");
    }
  };

  const handleClose = () => {
    dispatch(resetUpdateState());
    onClose();
  };

  const getSelectedUser = (userId) => {
    return users.find((u) => u.id === userId) || null;
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Initiative</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <TextField
              label="Title"
              fullWidth
              required
              value={formData.title}
              onChange={handleChange("title")}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Description"
              fullWidth
              multiline
              rows={3}
              value={formData.description}
              onChange={handleChange("description")}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Autocomplete
              options={users}
              getOptionLabel={(option) => option.full_name || ""}
              value={getSelectedUser(formData.owner_id)}
              onChange={handleOwnerChange("owner_id")}
              renderInput={(params) => (
                <TextField {...params} label="Owner" required />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Autocomplete
              options={users}
              getOptionLabel={(option) => option.full_name || ""}
              value={getSelectedUser(formData.secondary_owner_id)}
              onChange={handleOwnerChange("secondary_owner_id")}
              renderInput={(params) => (
                <TextField {...params} label="Secondary Owner" />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.status}
                label="Status"
                onChange={handleChange("status")}
              >
                <MenuItem value="future">Future</MenuItem>
                <MenuItem value="assigned">Assigned</MenuItem>
                <MenuItem value="working">Working</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Priority</InputLabel>
              <Select
                value={formData.priority}
                label="Priority"
                onChange={handleChange("priority")}
              >
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="critical">Critical</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Target Completion Date"
                value={formData.objective_completion_date}
                onChange={handleDateChange}
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
            </LocalizationProvider>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={update.isLoading}
        >
          {update.isLoading ? <CircularProgress size={24} /> : "Save Changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditInitiativeModal;
